import { useState, useEffect, useCallback } from 'react'
import { getPublicInvoice, markInvoiceViewed } from '@/services/api'
import type { PublicInvoiceResponse } from '@/types/invoice'

interface UseInvoiceResult {
  data: PublicInvoiceResponse | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useInvoice(paymentToken: string | undefined): UseInvoiceResult {
  const [data, setData] = useState<PublicInvoiceResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchInvoice = useCallback(async () => {
    if (!paymentToken) {
      setError('Invalid payment link')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const response = await getPublicInvoice(paymentToken)
      setData(response)

      // Mark as viewed if it's in 'sent' status
      if (response.invoice.status === 'sent') {
        await markInvoiceViewed(paymentToken).catch(() => {
          // Silently fail - viewing is non-critical
        })
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load invoice'
      
      // Handle specific error cases
      if (message.includes('404')) {
        setError('Invoice not found')
      } else if (message.includes('410')) {
        setError('This invoice has been cancelled')
      } else {
        setError(message)
      }
    } finally {
      setIsLoading(false)
    }
  }, [paymentToken])

  useEffect(() => {
    fetchInvoice()
  }, [fetchInvoice])

  return { data, isLoading, error, refetch: fetchInvoice }
}
